import React, { useState, memo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon as Iconify } from "@iconify/react";
import { Menu, X, LogOut } from "lucide-react";
import { cn } from "@/utils/cn";
import { useAuth } from "@/hooks/use-auth";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { Switch } from "@/components/ui/switch";

const menuStructure = [
  {
    section: "Main",
    items: [
      { name: "Dashboard", icon: "heroicons-solid:home", href: "/" },
    ]
  },
  {
    section: "Schedule",
    items: [
      { name: "My Calendar", icon: "heroicons-solid:calendar", href: "/schedule/calendar" },
      { name: "Appointments", icon: "heroicons-solid:clipboard-list", href: "/schedule/appointments" },
      { name: "Availability", icon: "heroicons-solid:clock", href: "/schedule/availability" },
    ]
  },
  {
    section: "Interact",
    items: [
      { name: "Messages", icon: "heroicons-solid:chat-bubble-left-right", href: "/interact/chat" },
    ]
  },
  {
    section: "Consultations",
    items: [
      { name: "Calls", icon: "heroicons-solid:phone", href: "/consultations/calls" },
      { name: "Chats", icon: "heroicons-solid:chat", href: "/consultations/chats" },
      { name: "My Clients", icon: "heroicons-solid:users", href: "/consultations/clients" },
    ]
  },
  {
    section: null, // No header, standalone item
    items: [
      { name: "Leads", icon: "heroicons-solid:user-plus", href: "/leads" },
    ]
  },
  {
    section: "Account",
    items: [
      { name: "Earnings", icon: "heroicons-solid:currency-rupee", href: "/earnings" },
      { name: "Reviews", icon: "heroicons-solid:star", href: "/reviews" },
      { name: "Profile", icon: "heroicons-solid:user-circle", href: "/profile" },
      { name: "Notifications", icon: "heroicons-solid:bell", href: "/notifications" },
      { name: "Help & Support", icon: "heroicons-solid:question-mark-circle", href: "/support" },
    ]
  }
];

const MenuItem = memo(({ item, isActive, onItemClick }) => (
  <li>
    <Link
      to={item.href}
      className={cn(
        "flex items-center justify-between px-4 py-2 rounded-lg transition-colors",
        isActive ? "text-gray-100 bg-primary/80" : "text-gray-600 hover:bg-gray-100"
      )}
      onClick={onItemClick}
    >
      <div className="flex items-center gap-3">
        <Iconify icon={item.icon} className="w-5 h-5" />
        <span className="text-sm font-normal">{item.name}</span>
      </div>
      {item.badge && (
        <span className="px-2 py-0.5 text-xs text-white bg-orange-500 rounded-full">
          {item.badge}
        </span>
      )}
    </Link>
  </li>
));

MenuItem.displayName = 'MenuItem';

const OnlineStatus = memo(() => {
  const { isOnline, toggleOnlineStatus } = useOnlineStatus();

  return (
    <div className="px-4 py-2">
      <div className="flex items-center justify-between bg-gray-100 rounded-full px-3 py-1.5">
        <span className={cn(
          "text-sm font-normal transition-colors",
          isOnline ? "text-green-600" : "text-gray-500"
        )}>
          {isOnline ? "Online" : "Offline"}
        </span>
        <Switch
          checked={isOnline}
          onCheckedChange={toggleOnlineStatus}
          className={cn(
            "ml-auto rounded-full transition-colors",
            "data-[state=checked]:bg-green-500",
            "data-[state=unchecked]:bg-gray-300"
          )}
        />
      </div>
    </div>
  );
});

OnlineStatus.displayName = 'OnlineStatus';

const AstroCoreItem = memo(() => (
  <div className="bg-gray-400 rounded-lg my-3 py-2 px-3">
    <div className="flex items-center justify-between text-white">
      <span className="text-xs">Coming Soon</span>
      <div className="flex items-center gap-2">
        <Iconify icon="heroicons-solid:star" className="w-4 h-4 text-orange-500" />
        <span className="text-base font-medium">AstroCore</span>
      </div>
    </div>
  </div>
));

AstroCoreItem.displayName = 'AstroCoreItem';

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();

  const toggleSidebar = useCallback(() => setIsOpen(prev => !prev), []);

  const isActive = useCallback((path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path;
  }, [location.pathname]);

  const handleItemClick = useCallback(() => {
    if (isOpen) {
      toggleSidebar();
    }
  }, [isOpen, toggleSidebar]);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-0 right-4 z-50 p-2 m-3 mt-4 rounded-lg bg-primary text-white md:hidden"
        aria-label={isOpen ? "Close menu" : "Open menu"}
      >
        {isOpen ? <X /> : <Menu />}
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full bg-white z-40 transition-all duration-300 ease-in-out flex flex-col",
          "shadow-lg w-64",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="p-4 border-b">
          <img
            src="/ApnaSanatan.svg"
            alt="apna-sanatan-logo"
            className="h-16"
          />
        </div>

        {/* Online Status Toggle */}
        <OnlineStatus />

        {/* Navigation */}
        <div className="flex-1 px-4 py-2 overflow-y-auto">
          {menuStructure.map((group, idx) => (
            <div key={group.section || `group-${idx}`} className="mb-3">
              {group.section && (
                <h3 className="text-xs font-medium text-gray-400 mb-1 px-4 uppercase tracking-wider">
                  {group.section}
                </h3>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <MenuItem
                    key={item.href}
                    item={item}
                    isActive={isActive(item.href)}
                    onItemClick={handleItemClick}
                  />
                ))}
              </ul>
            </div>
          ))}

          {/* AstroCore Section */}
          <AstroCoreItem />
        </div>

        {/* Logout Button */}
        <div className="p-3 border-t">
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-1.5 w-full text-orange-500 hover:bg-orange-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}
